import { call, put, takeLatest, ForkEffect, select } from "redux-saga/effects";
import { PayloadAction } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { actions } from "reducers/termsAndConditions";
import {
  getTermsVersions,
  uploadTermsVersion,
  downloadTermsVersion,
} from "middleware/termsAndConditions";
import {
  GetTermsVersionsPayloadType,
  UploadTermsVersionPayloadType,
  DownloadTermsVersionPayloadType,
} from "./types/termsAndConditions";
import { getValue } from "utils/utils";

function* getTermsVersionsSaga({ payload }: PayloadAction<GetTermsVersionsPayloadType>) {
  try {
    yield put(actions.getTermsVersionsRequest());
    
    const response: AxiosResponse<any> = yield call(
      getTermsVersions,
      payload.countryCode
    );
    
    const versionsData = response.data?.result;
    
    if (Array.isArray(versionsData)) {
      const normalizedVersions = versionsData.map((version: any) => {
        const normalized: any = { ...version };
        
        if (version && typeof version.uploadedBy === 'string') {
          normalized.uploadedBy = {
            email: version.uploadedBy
          };
        }
        
        if (!normalized.uploadDate && version.uploadedAt) {
          normalized.uploadDate = version.uploadedAt;
        }
        
        return normalized;
      });
      
      yield put(actions.getTermsVersionsSuccess(normalizedVersions));
    } else {
      yield put(actions.getTermsVersionsSuccess([]));
    }
  } catch (error: any) {
    if (error?.response?.status === 401) {
      if (!error.httpError) {
        error.httpError = true;
      }
      throw error;
    }
    yield put(
      actions.getTermsVersionsFailure(
        error?.response?.data?.message || "Error al cargar las versiones"
      )
    );
  }
}

function* uploadTermsVersionSaga({ payload }: PayloadAction<Omit<UploadTermsVersionPayloadType, 'userId'>>): Generator<any, void, any> {
  try {
    yield put(actions.setFileStatus({ loading: true, error: false }));
    
    const state: any = yield select();
    const countryCode = state.session?.countryCode || payload.countryCode;
    const tokens = getValue("tokens", true) || {};
    const token = tokens[countryCode];
    
    let userId = 0;
    if (token) {
      try {
        const tokenParts = token.split('.');
        if (tokenParts.length === 3) {
          const tokenPayload = JSON.parse(atob(tokenParts[1]));
          userId = tokenPayload.user_id || tokenPayload.userId || 0;
        }
      } catch (error) {
        console.error("Error al decodificar el token:", error);
      }
    }
    
    const uploadResponse: AxiosResponse<any> = yield call(
      uploadTermsVersion,
      payload.countryCode,
      payload.file,
      userId
    );
    
    if (uploadResponse.status !== 200 && uploadResponse.status !== 201) {
      const errorMessage = uploadResponse.data?.message || uploadResponse.data?.error || "";
      const isFileSizeError = errorMessage.toLowerCase().includes("file size") || 
                             errorMessage.toLowerCase().includes("too big") ||
                             errorMessage.toLowerCase().includes("demasiado pesado") ||
                             errorMessage.toLowerCase().includes("max size");
      
      if (isFileSizeError) {
        yield put(actions.setFileStatus({ 
          loading: false, 
          error: true,
          message: "El archivo es demasiado pesado"
        }));
        return;
      } else {
        yield put(actions.setFileStatus({ 
          loading: false, 
          error: true,
          message: errorMessage || "Error al subir el archivo"
        }));
        return;
      }
    }
    
    const reloadResponse: AxiosResponse<any> = yield call(
      getTermsVersions,
      payload.countryCode
    );
    
    const versionsData = reloadResponse.data?.result;
    
    if (Array.isArray(versionsData)) {
      const normalizedVersions = versionsData.map((version: any) => {
        const normalized: any = { ...version };
        
        if (version && typeof version.uploadedBy === 'string') {
          normalized.uploadedBy = {
            email: version.uploadedBy
          };
        }
        
        if (!normalized.uploadDate && version.uploadedAt) {
          normalized.uploadDate = version.uploadedAt;
        }
        
        return normalized;
      });
      
      yield put({ type: 'termsAndConditions/setVersions', payload: normalizedVersions });
    }
    
    yield put(actions.setFileStatus({ 
      loading: false, 
      error: false,
      message: "",
      justUploaded: true 
    }));
    yield put(actions.setCurrentFile(null));
  } catch (error: any) {
    if (error?.response?.status === 401) {
      if (!error.httpError) {
        error.httpError = true;
      }
      throw error;
    }
    
    if (error?.response?.status === 400) {
      const errorMessage = error?.response?.data?.message || "";
      const isFileSizeError = errorMessage.toLowerCase().includes("file size") || 
                             errorMessage.toLowerCase().includes("too big") ||
                             errorMessage.toLowerCase().includes("demasiado pesado");
      
      if (isFileSizeError) {
        yield put(actions.setFileStatus({ 
          loading: false, 
          error: true,
          message: "El archivo es demasiado pesado"
        }));
        return;
      }
    }
    
    yield put(actions.setFileStatus({ 
      loading: false, 
      error: true,
      message: error?.response?.data?.message || "Error al subir el archivo"
    }));
  }
}

function* downloadTermsVersionSaga({ payload }: PayloadAction<DownloadTermsVersionPayloadType>) {
  try {
    const response: AxiosResponse<Blob> = yield call(
      downloadTermsVersion,
      payload.countryCode,
      payload.versionId,
      payload.url
    );
    
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", payload.fileName || `terminos-y-condiciones-v${payload.versionId}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    if (error?.response?.status === 401) {
      if (!error.httpError) {
        error.httpError = true;
      }
      throw error;
    }
    console.error("Error al descargar el archivo:", error);
  }
}

const sagas: ForkEffect<never>[] = [
  takeLatest("termsAndConditions/getTermsVersions", getTermsVersionsSaga),
  takeLatest("termsAndConditions/uploadTermsVersion", uploadTermsVersionSaga),
  takeLatest("termsAndConditions/downloadTermsVersion", downloadTermsVersionSaga),
];

export default sagas;

